import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SignupPrompt from '../Account/SignupPrompt';
import {
    collection, getDocs, query, orderBy, where,
    doc, setDoc, deleteDoc, updateDoc, increment,
    limit, startAfter
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
import CoverRenderer from '../Storybook/CoverRenderer';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:         '#6f5900',
    tertiaryContainer:'#fed01b',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    surfaceHighest:   '#cfdef5',
    surfaceLowest:    '#ffffff',
    surfaceContainer: '#dce9fd',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
// ── Strip HTML tags from description ─────────────────────────────────────────
const stripHtml = (html: string): string =>
    html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const cleanDescription = (d?: string): string => {
    const text = stripHtml(d || '');
    return /^undefined/i.test(text) ? '' : text;
};

const publishedMs = (s: any): number =>
    s.publishedAt?.toMillis?.() ?? (s.publishedAt?.seconds ? s.publishedAt.seconds * 1000 : 0);
 
interface PublishedStory {
    storyId: string;
    userId: string;
    title: string;
    type: string;
    description: string;
    thumbnail?: any;
    content?: any;
    tags?: string[];
    authorPseudonym: string;
    authorAvatar: string;
    publishedAt: any;
    reads: number;
    wordCount?: number;
    reactions: { heart: number; star: number; smile: number; party: number };
}
 
// ── Lazy image ────────────────────────────────────────────────────────────────
const LazyImage: React.FC<{ src: string; alt: string; style?: React.CSSProperties }> = ({ src, alt, style }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);
 
    useEffect(() => {
        if (!imgRef.current) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsInView(true); observer.disconnect(); }
        }, { rootMargin: '50px' });
        observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, []);
 
    return (
        <img ref={imgRef} src={isInView ? src : ''} alt={alt}
            style={{ ...style, opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
            onLoad={() => setIsLoaded(true)} />
    );
};
 

 
// ── Genre badge colours ───────────────────────────────────────────────────────
const genreStyle = (tag?: string): React.CSSProperties => {
    const map: Record<string, { bg: string; color: string }> = {
        'Fantasy':   { bg: C.primaryContainer, color: C.primary },
        'Sci-Fi':    { bg: C.secondaryContainer, color: C.secondary },
        'Mystery':   { bg: '#dbeafe', color: '#1d4ed8' },
        'Adventure': { bg: C.tertiaryContainer, color: C.tertiary },
        'Funny':     { bg: '#fce7f3', color: '#9d174d' },
    };
    const entry = Object.entries(map).find(([k]) => tag?.includes(k));
    return entry
        ? { background: entry[1].bg, color: entry[1].color }
        : { background: C.surfaceHigh, color: C.onSurfaceVariant };
};
 
// ── Trending card ─────────────────────────────────────────────────────────────
const TrendingCard = React.memo<{
    story: PublishedStory;
    isLiked: boolean;
    currentUserId?: string;
    onLike: (e: React.MouseEvent, story: PublishedStory) => void;
    onClick: (story: PublishedStory) => void;
}>(({ story, isLiked, currentUserId, onLike, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const navigate = useNavigate();
    const firstTag = story.tags?.[0];
 
    return (
        <div
            onClick={() => onClick(story)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ background: C.surfaceLowest, borderRadius: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: hovered ? '0 12px 32px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.05)', transition: 'all 0.25s', transform: hovered ? 'translateY(-4px)' : 'translateY(0)', cursor: 'pointer', border: `1px solid ${hovered ? C.primaryContainer : 'transparent'}` }}
        >
            {/* Cover */}
<div style={{ position: 'relative', width: '100%',aspectRatio: '5/8', overflow: 'hidden' }}>
    <CoverRenderer
        coverData={
            story.thumbnail && typeof story.thumbnail === 'string'
                ? JSON.parse(story.thumbnail)
                : story.thumbnail
        }
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    />
    {/* Gradient overlay — separate div, not on CoverRenderer */}
    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(37,47,60,0.5) 0%, transparent 50%)', pointerEvents: 'none' }} />
    {/* Like button */}
    {currentUserId !== story.userId && (
        <button onClick={e => onLike(e, story)} style={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', transition: 'transform 0.15s', zIndex: 2 }}
            onMouseEnter={e => { e.stopPropagation(); (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}>
            {isLiked ? '❤️' : '🤍'}
        </button>
    )}
</div>
 
            {/* Info */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    {firstTag && (
                        <span style={{ ...genreStyle(firstTag), padding: '2px 10px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {firstTag.replace(/[^\w\s-]/g, '').trim()}
                        </span>
                    )}
                    {story.wordCount && (
                        <span style={{ fontSize: '0.72rem', color: C.onSurfaceVariant, fontWeight: '500' }}>
                            {Math.ceil(story.wordCount / 200)}m read
                        </span>
                    )}
                </div>
 
                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: hovered ? C.primary : C.onBackground, margin: '0 0 6px 0', lineHeight: '1.35', transition: 'color 0.15s' }}>
                    {story.title}
                </h3>
 
                {cleanDescription(story.description) && (
                    <p style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, margin: '0 0 12px 0', lineHeight: '1.55', flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',minHeight: '2.5em' }}>
                        {cleanDescription(story.description)}
                    </p>
                )}
 
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${C.surfaceLow}`, paddingTop: '12px', marginTop: 'auto' }}>
                <div
    onClick={e => { e.stopPropagation(); navigate(`/writer/${story.userId}`); }}
    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>
                            {story.authorAvatar}
                        </div>
                        <span style={{ fontSize: '0.78rem', fontWeight: '600', color: C.onSurface }}>{story.authorPseudonym}</span>
                    </div>
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: '600' }}>❤️ {story.reactions.heart}</span>
                </div>
            </div>
        </div>
    );
});
TrendingCard.displayName = 'TrendingCard';
 
// ── Mini card (New Adventures strip) ─────────────────────────────────────────
const MiniCard = React.memo<{
    story: PublishedStory;
    isLiked: boolean;
    currentUserId?: string;
    onLike: (e: React.MouseEvent, story: PublishedStory) => void;
    onClick: (story: PublishedStory) => void;
}>(({ story, isLiked, currentUserId, onLike, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const navigate = useNavigate();
    return (
        <div
            onClick={() => onClick(story)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ cursor: 'pointer' }}
        >
            <div style={{ width: '100%',aspectRatio: '5/8', borderRadius: '20px', overflow: 'hidden', marginBottom: '10px', boxShadow: hovered ? '0 8px 20px rgba(0,0,0,0.12)' : '0 2px 6px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s', position: 'relative' }}>
            <CoverRenderer
    coverData={
        story.thumbnail && typeof story.thumbnail === 'string'
            ? JSON.parse(story.thumbnail)
            : story.thumbnail
    }
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: hovered ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.4s' }}
/>
                {currentUserId !== story.userId && (
                    <button onClick={e => onLike(e, story)} style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.8rem', zIndex: 2 }}>
                        {isLiked ? '❤️' : '🤍'}
                    </button>
                )}
            </div>
            <h4 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '700', fontSize: '0.88rem', color: hovered ? C.primary : C.onBackground, margin: '0 0 3px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'color 0.15s' }}>
                {story.title}
            </h4>
            <p
    onClick={e => { e.stopPropagation(); navigate(`/writer/${story.userId}`); }}
    style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, fontWeight: '500', margin: 0, cursor: 'pointer' }}
>
    {story.authorAvatar} {story.authorPseudonym}
</p>
        </div>
    );
});
MiniCard.displayName = 'MiniCard';

 
// ── Main component ─────────────────────────────────────────────────────────────
const FeaturedStories: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [showSignupPrompt, setShowSignupPrompt] = useState(false);
    const [stories, setStories] = useState<PublishedStory[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [likedStories, setLikedStories] = useState<Set<string>>(new Set());
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const STORIES_PER_PAGE = 20;

    const [followedIds, setFollowedIds] = useState<string[]>([]);
    const [followingStories, setFollowingStories] = useState<PublishedStory[]>([]);
    const [followingLoading, setFollowingLoading] = useState(false);
 
    const categories = ['All', ...(currentUser && followedIds.length > 0 ? ['Following'] : []), 'Fantasy', 'Funny', 'Adventure', 'Mystery', 'Sci-Fi'];
 
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    const calculateHotness = useCallback((story: any): number => {
        try {
            const hearts = story.reactions?.heart || 0;
            const millis = story.publishedAt?.toMillis?.()
                ?? (story.publishedAt?.seconds ? story.publishedAt.seconds * 1000 : null)
                ?? (story.publishedAt instanceof Date ? story.publishedAt.getTime() : null)
                ?? Date.now();
            const hours = (Date.now() - millis) / 3600000;
            return hearts / Math.pow(hours + 2, 1.5);
        } catch {
            return 0;
        }
    }, []);
 
    const diversifyStories = useCallback((list: any[], max = 3): any[] => {
        const counts = new Map<string, number>();
        return list.filter(s => {
            const id = s.userId || s.authorId;
            const n = counts.get(id) || 0;
            if (n < max) { counts.set(id, n + 1); return true; }
            return false;
        });
    }, []);
 
    useEffect(() => {
        const load = async () => {
            try {
                const q = query(
                    collection(db, 'publishedStories'),
                    where('publishedAt', '>', new Date(0)),
                    orderBy('publishedAt', 'desc'),
                    limit(STORIES_PER_PAGE)
                );
                const snap = await getDocs(q);
                let list = snap.docs.map(d => {
                    try {
                        return { ...d.data(), storyId: d.id, hotness: calculateHotness(d.data()) } as any;
                    } catch {
                        return null;
                    }
                }).filter(Boolean);
                list.sort((a: any, b: any) => b.hotness - a.hotness);
                list = diversifyStories(list, 3);
                setStories(list);
                setLastVisible(snap.docs[snap.docs.length - 1]);
                setHasMore(snap.docs.length === STORIES_PER_PAGE);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [calculateHotness, diversifyStories]);
 
    useEffect(() => {
        if (!currentUser) return;
        getDocs(query(collection(db, 'likes'), where('userId', '==', currentUser.uid)))
            .then(snap => setLikedStories(new Set(snap.docs.map(d => d.data().storyId))))
            .catch(console.error);
    }, [currentUser]);

    // Who does this kid follow?
useEffect(() => {
    if (!currentUser) { setFollowedIds([]); return; }
    getDocs(query(collection(db, 'follows'), where('followerId', '==', currentUser.uid), limit(30)))
        .then(snap => setFollowedIds(snap.docs.map(d => d.data().followedId as string)))
        .catch(console.error);
}, [currentUser]);

// Load their stories when the Following pill is selected
useEffect(() => {
    if (selectedCategory !== 'Following' || followedIds.length === 0) return;
    let cancelled = false;
    setFollowingLoading(true);
    getDocs(query(collection(db, 'publishedStories'), where('userId', 'in', followedIds)))
        .then(snap => {
            if (cancelled) return;
            const list = snap.docs.map(d => ({ ...d.data(), storyId: d.id } as any));
            list.sort((a: any, b: any) => publishedMs(b) - publishedMs(a));
            setFollowingStories(list);
        })
        .catch(console.error)
        .finally(() => { if (!cancelled) setFollowingLoading(false); });
    return () => { cancelled = true; };
}, [selectedCategory, followedIds]);
 
    const handleLike = useCallback(async (e: React.MouseEvent, story: PublishedStory) => {
        e.stopPropagation();
        if (!currentUser) { setShowSignupPrompt(true); return; }
        if (currentUser.uid === story.userId) return;
        const liked = likedStories.has(story.storyId);
        const likeRef = doc(db, 'likes', `${story.storyId}_${currentUser.uid}`);
        const storyRef = doc(db, 'publishedStories', story.storyId);
        try {
            if (liked) {
                await deleteDoc(likeRef);
                await updateDoc(storyRef, { 'reactions.heart': increment(-1) });
                setLikedStories(prev => { const s = new Set(prev); s.delete(story.storyId); return s; });
                setStories(prev => prev.map(s => s.storyId === story.storyId ? { ...s, reactions: { ...s.reactions, heart: Math.max(0, s.reactions.heart - 1) } } : s));
                setFollowingStories(prev => prev.map(s => s.storyId === story.storyId ? { ...s, reactions: { ...s.reactions, heart: Math.max(0, s.reactions.heart - 1) } } : s));
            } else {
                await setDoc(likeRef, { storyId: story.storyId, userId: currentUser.uid, likedAt: new Date() });
                await updateDoc(storyRef, { 'reactions.heart': increment(1) });
                setLikedStories(prev => new Set(prev).add(story.storyId));
                setStories(prev => prev.map(s => s.storyId === story.storyId ? { ...s, reactions: { ...s.reactions, heart: s.reactions.heart + 1 } } : s));
                setFollowingStories(prev => prev.map(s => s.storyId === story.storyId ? { ...s, reactions: { ...s.reactions, heart: s.reactions.heart + 1 } } : s));
            }
        } catch (err) { console.error(err); }
    }, [currentUser, likedStories]);
 
    const handleStoryClick = useCallback((story: PublishedStory) => {
        navigate(`/read-story/${story.storyId}`, { state: { story } });
    }, [navigate]);
 
    const filteredStories = useMemo(() => stories.filter(s => {
        const matchCat = selectedCategory === 'All' || (s.tags?.some(t => t.includes(selectedCategory)));
        const matchSearch = !debouncedSearch ||
            s.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            s.tags?.some(t => t.toLowerCase().includes(debouncedSearch.toLowerCase()));
        return matchCat && matchSearch;
    }), [stories, selectedCategory, debouncedSearch]);

    const followingFiltered = followingStories.filter(s =>
        !debouncedSearch ||
        s.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        s.tags?.some(t => t.toLowerCase().includes(debouncedSearch.toLowerCase()))
    );
 
    const loadMore = async () => {
        if (!lastVisible || !hasMore || loadingMore) return;
        setLoadingMore(true);
        try {
            const q = query(
                collection(db, 'publishedStories'),
                orderBy('publishedAt', 'desc'),
                startAfter(lastVisible),
                limit(STORIES_PER_PAGE)
            );
            const snap = await getDocs(q);
            const newList = snap.docs.map(d => ({ ...d.data(), storyId: d.id, hotness: calculateHotness(d.data()) } as any));
            newList.sort((a: any, b: any) => b.hotness - a.hotness);
    
            setStories(prev => {
                const seen = new Set(prev.map(s => s.storyId));
                const fresh = diversifyStories(newList, 3).filter((s: any) => !seen.has(s.storyId));
                return [...prev, ...fresh] as PublishedStory[];
            });
    
            if (snap.docs.length > 0) setLastVisible(snap.docs[snap.docs.length - 1]);
            setHasMore(snap.docs.length === STORIES_PER_PAGE);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingMore(false);
        }
    };
 
    // Split stories for layout sections
    const editorPick  = filteredStories[0];
    const secondaryPicks = filteredStories.slice(1, 3);
    const trending    = filteredStories.slice(4, 8);
    const newAdventures = filteredStories.slice(8);
 
    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontSize: '2rem', background: C.surface }}>
            Loading stories... 📚
        </div>
    );
 
    return (
        <div style={{ background: C.surface, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", paddingBottom: isMobile ? '80px' : '60px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 48px' }}>
 
                {/* ── Search + filters ──────────────────────────────────────── */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', alignItems: isMobile ? 'stretch' : 'center', marginBottom: '48px' }}>
                    <div style={{ position: 'relative', flex: 1, maxWidth: isMobile ? '100%' : '480px' }}>
                        <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.1rem', color: C.onSurfaceVariant, pointerEvents: 'none' }}>🔍</span>
                        <input
                            type="text"
                            placeholder="Search for stories, magic, or authors..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{ width: '100%', paddingLeft: '44px', paddingRight: '16px', paddingTop: '14px', paddingBottom: '14px', background: C.surfaceLow, border: 'none', borderRadius: '14px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.onSurface, transition: 'background 0.15s' }}
                            onFocus={e => { e.target.style.background = C.surfaceLowest; e.target.style.boxShadow = `0 0 0 2px ${C.primaryContainer}`; }}
                            onBlur={e  => { e.target.style.background = C.surfaceLow;    e.target.style.boxShadow = 'none'; }}
                        />
                    </div>
 
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', flexShrink: 0, paddingBottom: '2px' }}>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                style={{ padding: '10px 20px', borderRadius: '9999px', border: 'none', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s',
                                    background: selectedCategory === cat ? C.primaryContainer : C.surfaceHigh,
                                    color:      selectedCategory === cat ? C.primary          : C.onSurfaceVariant,
                                }}
                                onMouseEnter={e => { if (selectedCategory !== cat) e.currentTarget.style.background = C.surfaceHighest; }}
                                onMouseLeave={e => { if (selectedCategory !== cat) e.currentTarget.style.background = C.surfaceHigh; }}
                            >
                                {cat === 'Following' ? '👥 Following' : cat}
                            </button>
                        ))}
                    </div>
                </div>
 
                {selectedCategory === 'Following' ? (
    followingLoading ? (
        <div style={{ textAlign: 'center', padding: '80px 24px', color: C.onSurfaceVariant, fontWeight: 600 }}>
            Loading stories from writers you follow... 📚
        </div>
    ) : followingFiltered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 24px', background: C.surfaceLowest, borderRadius: '24px' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>👥</div>
            <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onBackground, margin: '0 0 10px 0' }}>
                {searchTerm ? `No results for "${searchTerm}"` : 'Nothing new yet'}
            </h3>
            <p style={{ color: C.onSurfaceVariant, margin: 0 }}>
                {searchTerm ? 'Try a different search term!' : "The writers you follow haven't published any stories yet."}
            </p>
        </div>
    ) : (
        <section style={{ marginBottom: '56px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.onBackground, margin: 0, fontStyle: 'italic' }}>
                    From Writers You Follow
                </h2>
                <div style={{ flex: 1, height: '2px', background: C.surfaceHigh }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '20px' }}>
                {followingFiltered.map(story => (
                    <TrendingCard
                        key={story.storyId}
                        story={story}
                        isLiked={likedStories.has(story.storyId)}
                        currentUserId={currentUser?.uid}
                        onLike={handleLike}
                        onClick={handleStoryClick}
                    />
                ))}
            </div>
        </section>
    )
) : filteredStories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 24px', background: C.surfaceLowest, borderRadius: '24px' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📚</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onBackground, margin: '0 0 10px 0' }}>
                            {searchTerm ? `No results for "${searchTerm}"` : `No ${selectedCategory} stories yet`}
                        </h3>
                        <p style={{ color: C.onSurfaceVariant, margin: 0 }}>
                            {searchTerm ? 'Try a different search term!' : 'Be the first to publish one!'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* ── Editor's Choice bento ───────────────────────── */}
                        {filteredStories.slice(0, 4).length > 0 && (
    <section style={{ marginBottom: '56px' }}>
        <div style={{ marginBottom: '24px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.primary, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif" }}>
                Curated by Scriblish
            </span>
            <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '2rem' : '2.6rem', fontWeight: '900', color: C.onBackground, margin: '4px 0 0 0', fontStyle: 'italic' }}>
                Editor's Choice
            </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '20px'}}>
            {filteredStories.slice(0, 4).map((story, idx) => (
                <div key={story.storyId}
                    onClick={() => handleStoryClick(story)}
                    style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '12px' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                    {/* Portrait cover */}
                    <div style={{ position: 'relative', width: '100%',aspectRatio: '5/8', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 28px rgba(0,0,0,0.15)', transition: 'box-shadow 0.2s' }}>
                        <CoverRenderer
                            coverData={
                                story.thumbnail && typeof story.thumbnail === 'string'
                                    ? JSON.parse(story.thumbnail)
                                    : story.thumbnail
                            }
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                        />
                        {/* Featured badge on first */}
                        {idx === 0 && (
                            <div style={{ position: 'absolute', top: 12, left: 12, background: C.tertiaryContainer, color: C.tertiary, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: "'Epilogue', sans-serif" }}>
                                ⭐ Top Pick
                            </div>
                        )}
                        {/* Like button */}
                        {currentUser?.uid !== story.userId && (
                            <button onClick={e => handleLike(e, story)}
                                style={{ position: 'absolute', top: 10, right: 10, width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.85rem', zIndex: 2 }}>
                                {likedStories.has(story.storyId) ? '❤️' : '🤍'}
                            </button>
                        )}
                    </div>

                   {/* Info below */}
<div style={{ background: C.surfaceLowest, borderRadius: '12px', padding: '12px 14px', border: `1px solid ${C.surfaceHigh}` }}>
    {story.tags?.[0] && (
        <span style={{ ...genreStyle(story.tags[0]), padding: '2px 9px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-block', marginBottom: '6px' }}>
            {story.tags[0].replace(/[^\w\s-]/g, '').trim()}
        </span>
    )}
    <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, margin: '0 0 6px', lineHeight: '1.3' }}>
        {story.title}
    </h3>
    {story.description && (
        <p style={{ fontSize: '0.78rem', color: C.onSurfaceVariant, margin: '0 0 10px', lineHeight: '1.55', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',minHeight: '2.5em' }}>
            {stripHtml(story.description)}
        </p>
    )}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${C.surfaceLow}`, paddingTop: '8px' }}>
    <div
    onClick={e => { e.stopPropagation(); navigate(`/writer/${story.userId}`); }}
    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>{story.authorAvatar}</div>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: C.onSurfaceVariant }}>{story.authorPseudonym}</span>
        </div>
        <span style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '600' }}>❤️ {story.reactions.heart}</span>
    </div>
</div>
                </div>
            ))}
        </div>
    </section>
)}

 
                        {/* ── Trending this week ───────────────────────────── */}
                        {trending.length > 0 && (
                            <section style={{ marginBottom: '56px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.onBackground, margin: 0, fontStyle: 'italic' }}>
                                        Trending This Week
                                    </h2>
                                    <div style={{ flex: 1, height: '2px', background: C.surfaceHigh }} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '20px' }}>
                                    {trending.map(story => (
                                        <TrendingCard
                                            key={story.storyId}
                                            story={story}
                                            isLiked={likedStories.has(story.storyId)}
                                            currentUserId={currentUser?.uid}
                                            onLike={handleLike}
                                            onClick={handleStoryClick}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
 
                        {/* ── New adventures ───────────────────────────────── */}
                        {newAdventures.length > 0 && (
                            <section style={{ marginBottom: '48px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.onBackground, margin: 0, fontStyle: 'italic' }}>
                                        New Adventures
                                    </h2>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '20px'}}>
                                    {newAdventures.map(story => (
                                        <MiniCard
                                            key={story.storyId}
                                            story={story}
                                            isLiked={likedStories.has(story.storyId)}
                                            currentUserId={currentUser?.uid}
                                            onLike={handleLike}
                                            onClick={handleStoryClick}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
 
                        {/* ── Load more ────────────────────────────────────── */}
                        <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                            {hasMore ? (
                                <button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    style={{ background: loadingMore ? C.outlineVariant : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '14px 40px', fontSize: '0.95rem', fontWeight: '700', cursor: loadingMore ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: loadingMore ? 'none' : `0 4px 14px ${C.primary}40`, transition: 'opacity 0.15s' }}
                                    onMouseEnter={e => { if (!loadingMore) e.currentTarget.style.opacity = '0.9'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                >
                                    {loadingMore ? '⏳ Loading...' : '📚 Load More Stories'}
                                </button>
                            ) : stories.length > 0 ? (
                                <div style={{ color: C.onSurfaceVariant, fontSize: '0.95rem', fontWeight: '600' }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎉</div>
                                    You have reached the end! {stories.length} stories total.
                                </div>
                            ) : null}
                        </div>
                    </>
                )}
            </div>
 
            <SignupPrompt isOpen={showSignupPrompt} onClose={() => setShowSignupPrompt(false)} reason="like" />
        </div>
    );
};
 
export default FeaturedStories;